/**
 * HubSpot tracking code + Customer Agent chat widget (RDT-307).
 *
 * HubSpot's install guide says to paste a <script> before </body>. Doing that in index.html would
 * load it on every environment, including localhost and the CI e2e stack, and would pollute the
 * marketing analytics with our own test traffic. Loading it from the bootstrap instead lets the
 * portal id come from .env.<mode>, so only the stages that set VITE_APP_HUBSPOT_PORTAL_ID get it.
 *
 * The loader is a single script; it pulls in analytics, the ads pixel, the cookie banner and the
 * conversations (chat) embed itself. Where an app ships a Content-Security-Policy (see its nginx.conf
 * and the edge Traefik), those secondary hosts must be allowed or the browser drops them silently.
 *
 * Deliberately NOT here: any identify call. Trainers are not to be identified to HubSpot; the widget
 * runs on the anonymous tracking cookie only.
 */

/** The id HubSpot's own snippet gives the loader tag; its sub-loaders check for it, so keep it. */
export const HUBSPOT_SCRIPT_ID = "hs-script-loader";

/** persolog's HubSpot account lives in the EU data centre, hence the eu1 host. */
export function hubSpotScriptUrl(portalId: string): string {
    return `https://js-eu1.hs-scripts.com/${encodeURIComponent(portalId)}.js`;
}

/**
 * Appends the HubSpot loader to the document, once. Returns the script element, or null when no
 * portal id is configured for this build (localhost, ci, dev) or the document has no body yet.
 */
export function loadHubSpot(portalId: string | undefined, doc: Document = document): HTMLScriptElement | null {
    const id = portalId?.trim();
    if (!id) {
        return null;
    }

    const existing = doc.getElementById(HUBSPOT_SCRIPT_ID);
    if (existing instanceof HTMLScriptElement) {
        return existing;
    }

    if (!doc.body) {
        return null;
    }

    const script = doc.createElement("script");
    script.id = HUBSPOT_SCRIPT_ID;
    script.type = "text/javascript";
    script.async = true;
    script.defer = true;
    script.src = hubSpotScriptUrl(id);
    doc.body.appendChild(script);
    return script;
}
